<template>
  <div class="hotel-row">
    <div class="hotel-photo">
      <img class="photo-main" :src="hotel.image" :alt="hotel.name" />
      <div class="photo-gradient" />
      <span class="photo-brand">{{ hotel.brand }}</span>
      <div class="partner-badge">
        <span class="badge-icon" />
        <span class="badge-text">
          <em>IWMC 2027 · OFFICIAL</em>
          <strong>PARTNER HOTEL</strong>
        </span>
      </div>
    </div>
    <div class="hotel-card">
      <div class="hotel-card-head">
        <h2>{{ hotel.name }}</h2>
        <div class="star-row">
          <svg
            v-for="star in hotel.stars"
            :key="star"
            viewBox="0 0 20 20"
            class="star"
          >
            <path d="M10 2.4l7.6 7.6-7.6 7.6L2.4 10 10 2.4z" />
          </svg>
        </div>
      </div>
      <p class="hotel-room">
        <svg viewBox="0 0 16 16" class="row-icon">
          <path
            d="M2 4.5h9.5a2.5 2.5 0 012.5 2.5v.5H2v-3zM1 8.5h14M2.5 8.5V12M13.5 8.5V12"
            fill="none"
            stroke="currentColor"
            stroke-width="1.2"
            stroke-linecap="round"
          />
        </svg>
        <span>{{ hotel.room }}</span>
      </p>
      <p v-if="hotel.distance" class="hotel-distance">
        <svg viewBox="0 0 16 16" class="row-icon">
          <path
            d="M8 1.8a4.6 4.6 0 014.6 4.6c0 3.2-4.6 7.8-4.6 7.8S3.4 9.6 3.4 6.4A4.6 4.6 0 018 1.8z"
            fill="none"
            stroke="currentColor"
            stroke-width="1.2"
          />
          <circle cx="8" cy="6.4" r="1.7" fill="currentColor" />
        </svg>
        <span>{{ hotel.distance }}</span>
      </p>
      <div class="price-row">
        <div class="price-block">
          <p class="price-label">
            {{ t("shell.worldmc.home.portalPrice") }}
          </p>
          <p class="price-line">
            <strong class="price-amount">{{ hotel.portalPrice }}</strong>
            <span>{{ hotel.portalPriceNote }}</span>
          </p>
        </div>
        <button type="button" class="view-rooms" @click="emit('select', hotel)">
          {{ t("shell.worldmc.home.viewRooms") }} →
        </button>
      </div>
      <div class="bar-rate">
        <p class="bar-title">
          {{ t("shell.worldmc.home.barRate") }}
        </p>
        <p class="bar-items">
          <span>{{ hotel.bar[0] }}</span>
          <i />
          <span>{{ hotel.bar[1] }}</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from "vue-i18n";

defineProps({
  /** 渠道配置里固定写死的酒店数据（types.WorldmcFeaturedHotel） */
  hotel: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(["select"]);

const { t } = useI18n();

defineOptions({ name: "WorldmcFeaturedHotel" });
</script>

<style lang="scss" scoped>
.hotel-row {
  display: grid;
  grid-template-columns: 616fr 680fr;
  margin-bottom: 25px;
}

.hotel-photo {
  position: relative;
  min-height: 318px;
  background: #d9d9d9;
}

.hotel-photo .photo-main {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-gradient {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 109px;
  background: linear-gradient(
    180deg,
    rgba(2, 30, 60, 0) 0%,
    rgba(2, 30, 60, 0.78) 100%
  );
}

.photo-brand {
  position: absolute;
  bottom: 24px;
  left: 28px;
  color: #fff;
  font-family: "Cormorant-Bold", "Cormorant", serif;
  font-size: 22px;
  letter-spacing: 0.06em;
}

.partner-badge {
  position: absolute;
  /* 设计稿：徽章骑跨照片左上角，上缘超出照片顶边约 6px（需保持 photo 无 overflow 裁剪） */
  top: -6px;
  left: 13px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 12px;
  border: 1px solid #bea57b;
  background: rgba(44, 30, 8, 0.92);
}

.badge-icon {
  width: 8px;
  height: 8px;
  background: #f0b14c;
  transform: rotate(45deg);
}

.badge-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.badge-text em {
  color: #f0b14c;
  font-family: "lato", sans-serif;
  font-size: 9px;
  font-style: normal;
  letter-spacing: 0.28em;
  line-height: 1;
}

.badge-text strong {
  color: #f2dfc1;
  font-family: "Josefin Sans", "lato", sans-serif;
  font-size: 11px;
  letter-spacing: 0.32em;
  line-height: 1;
}

.hotel-card {
  position: relative;
  padding: 28px 44px 24px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(13, 31, 43, 0.08);
}

.hotel-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.hotel-card h2 {
  margin: 0 0 24px;
  font-family: "Cormorant-Bold", "Cormorant", serif;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
}

.star-row {
  display: flex;
  gap: 4px;
  padding-top: 4px;
}

.star {
  width: 16px;
  height: 16px;
  fill: #1876ae;
}

.hotel-room,
.hotel-distance {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 10px;
  color: #0a4486;
  font-family: "lato", sans-serif;
  font-size: 13px;
}

.row-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}

.price-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}

.price-block {
  margin: 40px 0 0;
  min-height: 43px;
}

.price-label {
  margin: 0 0 2px;
  color: #0a4486;
  font-family: "lato", sans-serif;
  font-size: 12px;
  font-weight: 700;
}

.price-line {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 0;
}

.price-line :deep(.y-price) {
  font-family: "BarlowCondensed-SemiBold", sans-serif;
  font-weight: 600;
}

/* 门户价格固定写死（EUR），纯文案渲染 */
.price-amount {
  color: #0a4486;
  font-family: "BarlowCondensed-SemiBold", "lato", sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 1;
  /* flex 子项被压缩时 "EUR 155" 会在空格处折行，价格必须单行（CDEH-63834 二轮） */
  white-space: nowrap;
}

.price-line span {
  color: #0a4486;
  font-family: "lato", sans-serif;
  font-size: 13px;
}

.view-rooms {
  flex-shrink: 0;
  height: 28px;
  padding: 0 18px;
  border: 0;
  color: #fff;
  background: #0a4486;
  cursor: pointer;
  font-family: "lato", sans-serif;
  font-size: 12px;
  letter-spacing: 0.04em;
}

.view-rooms:hover {
  background: #1876ae;
}

.bar-rate {
  margin-top: 20px;
  padding: 6px 14px 10px;
  border: 1px solid #d5e1e8;
  background: #f6f9fa;
}

.bar-title {
  margin: 0 0 4px;
  color: #294757;
  font-family: "lato", sans-serif;
  font-size: 12px;
  font-weight: 700;
}

.bar-items {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  color: #3d789b;
  font-family: "lato", sans-serif;
  font-size: 12px;
}

.bar-items i {
  width: 1px;
  height: 14px;
  background: #d9d9d9;
}

/* ---- Responsive ---- */
@media screen and (max-width: 1280px) {
  .hotel-row {
    grid-template-columns: 1fr 1.1fr;
  }
}

@media screen and (max-width: 1000px) {
  .hotel-row {
    grid-template-columns: 1fr;
  }
}

/* ---- Mobile（D-25）---- */
@media screen and (max-width: 768px) {
  .hotel-card {
    padding: 24px 20px 20px;
  }

  .hotel-card h2 {
    margin-bottom: 16px;
    font-size: 22px;
  }

  .price-row {
    flex-wrap: wrap;
  }

  .price-block {
    margin-top: 24px;
  }

  .bar-items {
    flex-wrap: wrap;
    gap: 6px 12px;
  }
}
</style>
